import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { AndCondition, CheckboxGroupModel, CheckedCondition, ConditionalValue, ConditionID, ConditionModel, EqConditionModel, GtCondition, InArrayCondition, LtCondition, NotBlankCondition, NotConditionModel, NumberCondition, OrCondition, PatternConditionModel, PickID, TruthyCondition, ValueID } from '../../form-json.types';
import { DataService } from '../data/data.service';
import { FormCoreModule } from '../form-core.module';
import { ValueService } from '../value/value.service';

@Injectable({
  providedIn: FormCoreModule
})
export class ConditionService {

  constructor(private value: ValueService, private data: DataService) { }

  assign(condition: ConditionID) {
    const model = this.data.getModel<ConditionModel>(condition)
    switch (model.type) {
      case 'eq-condition':
        return this.eqCondition(model)
      case 'not-condition':
        return this.notCondition(model)
      case 'pattern-condition':
        return this.patternCondition(model)
      case 'in-array-condition':
        return this.inArrayCondition(model)
      case 'and-condition':
        return this.andCondition(model)
      case 'truthy-condition':
        return this.truthyCondition(model)
      case 'or-condition':
        return this.orCondition(model)
      case 'not-blank-condition':
        return this.notBlankCondition(model)
      case 'number-condition':
        return this.numberCondition(model)
      case 'gt-condition':
        return this.gtCondition(model)
      case 'lt-condition':
        return this.ltCondition(model)
      case 'checked-condition':
        return this.checkedCondition(model)
    }
  }

  private checkedCondition(checked: CheckedCondition): boolean {
    return Validators.required(this.data.getControl(this.data.getModel<CheckboxGroupModel>(checked.relationships.component.data).attributes.path)) === null
  }

  private eqCondition(eq: EqConditionModel): boolean {
    return this.getValue(eq.relationships.lhs.data) === this.getValue(eq.relationships.rhs.data)
  }

  private notCondition(no: NotConditionModel): boolean {
    return !this.assign(no.relationships.condition.data)
  }

  private patternCondition(pattern: PatternConditionModel): boolean {
    const _pattern = pattern.attributes.pattern
    const value = this.getValue(pattern.relationships.value.data)
    if (!_pattern) return true
    let regex: RegExp
    let regexStr: string = ''
    if (_pattern.charAt(0) !== '^') regexStr += '^';
    regexStr += _pattern;
    if (_pattern.charAt(_pattern.length - 1) !== '$') regexStr += '$';
    regex = new RegExp(regexStr)
    return regex.test(value)
  }

  private inArrayCondition(inArray: InArrayCondition): boolean {
    const lhs = this.getValue(inArray.relationships.lhs.data)
    const rhs = this.getValue(inArray.relationships.rhs.data) as any[]
    return rhs.indexOf(lhs) > -1
  }

  private andCondition(and: AndCondition): boolean {
    return and.relationships.conditions.data.every(condition => this.assign(condition))
  }

  private truthyCondition(truthy: TruthyCondition): boolean {
    return !!this.getValue(truthy.relationships.value.data)
  }

  private orCondition(or: OrCondition): boolean {
    return or.relationships.conditions.data.some(condition => this.assign(condition))
  }

  private notBlankCondition(notBlank: NotBlankCondition): boolean {
    return Validators.required(this.getValue(notBlank.relationships.value.data)) === null
  }

  private numberCondition(number: NumberCondition): boolean {
    const value = this.getValue(number.relationships.value.data)
    return !isNaN(parseFloat(value)) && isFinite(value)
  }

  private gtCondition(gt: GtCondition): boolean {
    const lhs = this.getValue(gt.relationships.lhs.data)
    const rhs = this.getValue(gt.relationships.rhs.data)
    return lhs > rhs
  }

  private ltCondition(lt: LtCondition): boolean {
    const lhs = this.getValue(lt.relationships.lhs.data)
    const rhs = this.getValue(lt.relationships.rhs.data)
    return lhs < rhs
  }

  private conditionalValue(conditional: ConditionalValue): any {
    return this.assign(conditional.relationships.condition.data) ? this.getValue(conditional.relationships.truthy.data) : this.getValue(conditional.relationships.falsy.data)
  }

  private getValue(value: ValueID | PickID<ConditionalValue>) {
    return value.type !== 'conditional-value' ? this.value.assign(value) : this.conditionalValue(this.data.getModel<ConditionalValue>(value))
  }
}
